import { useState } from 'react';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import {
  useSlaConfigurations,
  useSlaBreaches,
  useSlaPerformance,
  useCreateSlaConfig,
  useUpdateSlaConfig,
  type SlaConfiguration,
  type CreateSlaConfigPayload,
} from './hooks/useSla';

/**
 * SlaDashboard — SLA configuration, breach alerts, and performance metrics.
 * Requirements: 19.1, 19.2
 */
export function SlaDashboard() {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<SlaConfiguration | null>(null);

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">SLA Management</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Configure service level agreements, monitor breaches, and track performance metrics.
        </p>
      </div>

      {/* Performance Metrics */}
      <SlaPerformancePanel />

      {/* Active Breaches */}
      <SlaBreachesPanel />

      {/* SLA Configurations */}
      <SlaConfigurationsPanel
        onCreateClick={() => setIsCreateModalOpen(true)}
        onEditClick={(config) => setEditingConfig(config)}
      />

      {/* Create Modal */}
      <SlaConfigModal
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        mode="create"
      />

      {/* Edit Modal */}
      {editingConfig && (
        <SlaConfigModal
          open={!!editingConfig}
          onClose={() => setEditingConfig(null)}
          mode="edit"
          config={editingConfig}
        />
      )}
    </div>
  );
}

// ─── Performance Panel ──────────────────────────────────────────────────────

function SlaPerformancePanel() {
  const { data, isLoading } = useSlaPerformance();

  if (isLoading) {
    return <LoadingCard label="Loading performance metrics..." />;
  }

  if (!data) {
    return (
      <Card>
        <CardContent>
          <p className="text-center text-gray-500 dark:text-gray-400">
            Unable to load performance metrics.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <MetricCard
        label="Compliance Rate"
        value={`${data.complianceRate.toFixed(1)}%`}
        variant={
          data.complianceRate >= 90 ? 'success' : data.complianceRate >= 70 ? 'warning' : 'danger'
        }
      />
      <MetricCard
        label="Jobs Meeting SLA"
        value={`${data.jobsMeetingSla} / ${data.totalJobs}`}
        variant="info"
      />
      <MetricCard
        label="Avg Completion Time"
        value={`${data.averageCompletionHours.toFixed(1)}h`}
        variant="default"
      />
      <MetricCard
        label="Active Breaches"
        value={String(data.breachCount)}
        variant={data.breachCount > 0 ? 'danger' : 'success'}
      />
    </div>
  );
}

// ─── Breaches Panel ─────────────────────────────────────────────────────────

function SlaBreachesPanel() {
  const { data: breaches, isLoading } = useSlaBreaches();

  if (isLoading) {
    return <LoadingCard label="Loading breach alerts..." />;
  }

  const activeBreaches = breaches ?? [];

  return (
    <Card>
      <CardHeader
        title="Active Breaches"
        description={`${activeBreaches.length} job${activeBreaches.length !== 1 ? 's' : ''} currently exceeding SLA`}
      />
      <CardContent>
        {activeBreaches.length === 0 ? (
          <div className="py-6 text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No active SLA breaches. All jobs are within target.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" aria-label="Active SLA breaches">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="pb-3 text-left font-medium text-gray-500 dark:text-gray-400">
                    Job
                  </th>
                  <th className="pb-3 text-left font-medium text-gray-500 dark:text-gray-400">
                    Type
                  </th>
                  <th className="pb-3 text-left font-medium text-gray-500 dark:text-gray-400">
                    Client
                  </th>
                  <th className="pb-3 text-left font-medium text-gray-500 dark:text-gray-400">
                    Assignee
                  </th>
                  <th className="pb-3 text-right font-medium text-gray-500 dark:text-gray-400">
                    Target
                  </th>
                  <th className="pb-3 text-right font-medium text-gray-500 dark:text-gray-400">
                    Elapsed
                  </th>
                  <th className="pb-3 text-left font-medium text-gray-500 dark:text-gray-400">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {activeBreaches.map((breach) => (
                  <tr key={breach.id}>
                    <td className="py-3 font-medium text-gray-900 dark:text-gray-100">
                      {breach.jobTitle}
                    </td>
                    <td className="py-3 text-gray-600 dark:text-gray-400">{breach.jobType}</td>
                    <td className="py-3 text-gray-600 dark:text-gray-400">{breach.clientName}</td>
                    <td className="py-3 text-gray-600 dark:text-gray-400">{breach.assigneeName}</td>
                    <td className="py-3 text-right text-gray-600 dark:text-gray-400">
                      {breach.targetHours}h
                    </td>
                    <td className="py-3 text-right font-medium text-danger-600 dark:text-danger-400">
                      {breach.elapsedHours.toFixed(1)}h
                    </td>
                    <td className="py-3">
                      <BreachStatusBadge status={breach.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Configurations Panel ───────────────────────────────────────────────────

interface SlaConfigurationsPanelProps {
  onCreateClick: () => void;
  onEditClick: (config: SlaConfiguration) => void;
}

function SlaConfigurationsPanel({ onCreateClick, onEditClick }: SlaConfigurationsPanelProps) {
  const { data: configs, isLoading } = useSlaConfigurations();

  if (isLoading) {
    return <LoadingCard label="Loading SLA configurations..." />;
  }

  const configurations = configs ?? [];

  return (
    <Card>
      <CardHeader
        title="SLA Configurations"
        description="Define target hours and escalation rules by job type"
        action={
          <Button size="sm" onClick={onCreateClick}>
            Add Configuration
          </Button>
        }
      />
      <CardContent>
        {configurations.length === 0 ? (
          <div className="py-6 text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No SLA configurations defined yet. Add one to start tracking turnaround times.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" aria-label="SLA configurations">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="pb-3 text-left font-medium text-gray-500 dark:text-gray-400">
                    Job Type
                  </th>
                  <th className="pb-3 text-right font-medium text-gray-500 dark:text-gray-400">
                    Target Hours
                  </th>
                  <th className="pb-3 text-right font-medium text-gray-500 dark:text-gray-400">
                    Warning Threshold
                  </th>
                  <th className="pb-3 text-right font-medium text-gray-500 dark:text-gray-400">
                    Escalation Timing
                  </th>
                  <th className="pb-3 text-left font-medium text-gray-500 dark:text-gray-400">
                    Status
                  </th>
                  <th className="pb-3 text-right font-medium text-gray-500 dark:text-gray-400">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {configurations.map((config) => (
                  <tr key={config.id}>
                    <td className="py-3 font-medium text-gray-900 dark:text-gray-100">
                      {config.jobType}
                    </td>
                    <td className="py-3 text-right text-gray-600 dark:text-gray-400">
                      {config.targetHours}h
                    </td>
                    <td className="py-3 text-right text-gray-600 dark:text-gray-400">
                      {config.warningThresholdPercent}%
                    </td>
                    <td className="py-3 text-right text-gray-600 dark:text-gray-400">
                      +{config.escalationTimingHours}h after breach
                    </td>
                    <td className="py-3">
                      <Badge variant={config.isActive ? 'success' : 'default'} size="sm" dot>
                        {config.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="py-3 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onEditClick(config)}
                        aria-label={`Edit ${config.jobType} configuration`}
                      >
                        Edit
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Config Modal ───────────────────────────────────────────────────────────

interface SlaConfigModalProps {
  open: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  config?: SlaConfiguration;
}

function SlaConfigModal({ open, onClose, mode, config }: SlaConfigModalProps) {
  const createMutation = useCreateSlaConfig();
  const updateMutation = useUpdateSlaConfig(config?.id ?? '');

  const [formData, setFormData] = useState<CreateSlaConfigPayload>({
    jobType: config?.jobType ?? '',
    targetHours: config?.targetHours ?? 24,
    warningThresholdPercent: config?.warningThresholdPercent ?? 80,
    escalationRecipients: config?.escalationRecipients ?? [],
    escalationTimingHours: config?.escalationTimingHours ?? 4,
  });

  const [recipientInput, setRecipientInput] = useState('');

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (mode === 'create') {
      createMutation.mutate(formData, {
        onSuccess: () => onClose(),
      });
    } else {
      updateMutation.mutate(formData, {
        onSuccess: () => onClose(),
      });
    }
  }

  function handleAddRecipient() {
    const email = recipientInput.trim();
    if (email && !formData.escalationRecipients.includes(email)) {
      setFormData((prev) => ({
        ...prev,
        escalationRecipients: [...prev.escalationRecipients, email],
      }));
      setRecipientInput('');
    }
  }

  function handleRemoveRecipient(email: string) {
    setFormData((prev) => ({
      ...prev,
      escalationRecipients: prev.escalationRecipients.filter((r) => r !== email),
    }));
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={mode === 'create' ? 'Create SLA Configuration' : 'Edit SLA Configuration'}
      description="Set turnaround targets and escalation rules for a job type."
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Job Type"
          placeholder="e.g., T1 Return, T2 Return, Bookkeeping"
          value={formData.jobType}
          onChange={(e) => setFormData((prev) => ({ ...prev, jobType: e.target.value }))}
          required
          disabled={mode === 'edit'}
        />

        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Target Hours"
            type="number"
            min={1}
            step={0.5}
            value={String(formData.targetHours)}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, targetHours: Number(e.target.value) }))
            }
            required
            hint="Total hours allowed to complete job"
          />
          <Input
            label="Warning Threshold (%)"
            type="number"
            min={1}
            max={99}
            value={String(formData.warningThresholdPercent)}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                warningThresholdPercent: Number(e.target.value),
              }))
            }
            required
            hint="Percentage of target to trigger warning"
          />
        </div>

        <Input
          label="Escalation Timing (hours after breach)"
          type="number"
          min={0}
          step={0.5}
          value={String(formData.escalationTimingHours)}
          onChange={(e) =>
            setFormData((prev) => ({
              ...prev,
              escalationTimingHours: Number(e.target.value),
            }))
          }
          required
          hint="Hours after breach before escalation notification"
        />

        {/* Escalation Recipients */}
        <div>
          <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Escalation Recipients
          </label>
          <div className="flex gap-2">
            <Input
              placeholder="email@example.com"
              value={recipientInput}
              onChange={(e) => setRecipientInput(e.target.value)}
              aria-label="Escalation recipient email"
            />
            <Button type="button" variant="outline" size="md" onClick={handleAddRecipient}>
              Add
            </Button>
          </div>
          {formData.escalationRecipients.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {formData.escalationRecipients.map((email) => (
                <Badge key={email} variant="info" size="sm">
                  {email}
                  <button
                    type="button"
                    onClick={() => handleRemoveRecipient(email)}
                    className="ml-1 text-info-700 hover:text-info-900 dark:text-info-300 dark:hover:text-info-100"
                    aria-label={`Remove ${email}`}
                  >
                    ×
                  </button>
                </Badge>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={isSubmitting}>
            {mode === 'create' ? 'Create' : 'Save Changes'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ─── Shared Sub-components ──────────────────────────────────────────────────

interface MetricCardProps {
  label: string;
  value: string;
  variant: 'success' | 'warning' | 'danger' | 'info' | 'default';
}

function MetricCard({ label, value, variant }: MetricCardProps) {
  const colorMap = {
    success: 'text-success-600 dark:text-success-400',
    warning: 'text-warning-600 dark:text-warning-400',
    danger: 'text-danger-600 dark:text-danger-400',
    info: 'text-info-600 dark:text-info-400',
    default: 'text-gray-900 dark:text-gray-100',
  };

  return (
    <Card>
      <CardContent className="mt-0">
        <p className="text-xs font-medium uppercase text-gray-500 dark:text-gray-400">{label}</p>
        <p className={`mt-1 text-2xl font-bold ${colorMap[variant]}`}>{value}</p>
      </CardContent>
    </Card>
  );
}

function BreachStatusBadge({ status }: { status: 'warning' | 'breached' | 'escalated' }) {
  const variants = {
    warning: 'warning' as const,
    breached: 'danger' as const,
    escalated: 'danger' as const,
  };

  const labels = {
    warning: 'Warning',
    breached: 'Breached',
    escalated: 'Escalated',
  };

  return (
    <Badge variant={variants[status]} size="sm" dot>
      {labels[status]}
    </Badge>
  );
}

function LoadingCard({ label }: { label: string }) {
  return (
    <Card>
      <CardContent>
        <div className="flex items-center justify-center py-6">
          <div className="flex items-center gap-2 text-gray-500" aria-live="polite">
            <svg
              className="h-5 w-5 animate-spin text-brand-500"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
              />
            </svg>
            {label}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
